import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import PanelLayout from '../layouts/PanelLayout';
import { appService } from '../../services/appService';
import { appReleaseService } from '../../services/appReleaseService';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import IconInput from '../../components/common/IconInput';
import SearchableSelect from '../../components/common/SearchableSelect';
import ConfirmModal from '../../components/common/ConfirmModal';
import Loader from '../../components/Loader';

const STATUS_OPTIONS = [
   { id: 'DRAFT', name: 'Draft' },
   { id: 'PUBLISHED', name: 'Published' },
   { id: 'ARCHIVED', name: 'Archived' },
];

const RELEASE_STATUS_OPTIONS = [
   { id: 'DRAFT', name: 'Draft' },
   { id: 'PUBLISHED', name: 'Published' },
];

const PACKAGE_TYPE_OPTIONS = [
   { id: 'DEB', name: 'DEB' },
   { id: 'RPM', name: 'RPM' },
   { id: 'APPIMAGE', name: 'AppImage' },
];

const emptyForm = {
   name: '',
   slug: '',
   tagline: '',
   content: '',
   disclaimer: '',
   repoUrl: '',
   status: 'DRAFT',
   order: '0',
   showInProjects: false,
};

const emptyRelease = {
   version: '',
   packageType: 'DEB',
   architecture: 'amd64',
   releaseNotes: '',
   status: 'PUBLISHED',
   file: null,
};

const formatBytes = (bytes) => {
   if (!bytes) return '0 B';
   const units = ['B', 'KB', 'MB', 'GB'];
   const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
   return `${(bytes / 1024 ** i).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const SectionCard = ({ title, children }) => (
   <div className="bg-white dark:bg-dark-secondary rounded-xl border border-gray-200 dark:border-gray-700 p-6">
      {title && (
         <h2 className="text-xs font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-5">
            {title}
         </h2>
      )}
      {children}
   </div>
);

const Label = ({ children, required }) => (
   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
      {children} {required && <span className="text-red-500">*</span>}
   </label>
);

const AppForm = () => {
   const { id } = useParams();
   const navigate = useNavigate();
   const isEdit = !!id;
   const fileInputRef = useRef(null);
   const screenshotInputRef = useRef(null);
   const packageInputRef = useRef(null);

   const [loading, setLoading] = useState(isEdit);
   const [saving, setSaving] = useState(false);
   const [uploading, setUploading] = useState(false);
   const [uploadingScreenshot, setUploadingScreenshot] = useState(false);
   const [formData, setFormData] = useState(emptyForm);
   const [logoPreview, setLogoPreview] = useState('');
   const [logoPath, setLogoPath] = useState('');
   const [features, setFeatures] = useState([]);
   const [screenshots, setScreenshots] = useState([]); // [{ path, url, alt }]
   const [releases, setReleases] = useState([]);
   const [newRelease, setNewRelease] = useState(emptyRelease);
   const [releaseUploadProgress, setReleaseUploadProgress] = useState(null);
   const [addingRelease, setAddingRelease] = useState(false);
   const [deleteReleaseTarget, setDeleteReleaseTarget] = useState(null);

   useEffect(() => {
      if (isEdit) fetchApp();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [id]);

   const fetchApp = async () => {
      try {
         setLoading(true);
         const res = await appService.getById(id);
         const a = res.data;
         setFormData({
            name: a.name || '',
            slug: a.slug || '',
            tagline: a.tagline || '',
            content: a.content || '',
            disclaimer: a.disclaimer || '',
            repoUrl: a.repoUrl || '',
            status: a.status || 'DRAFT',
            order: String(a.order ?? 0),
            showInProjects: !!a.showInProjects,
         });
         setFeatures(Array.isArray(a.features) ? a.features : []);
         setScreenshots(
            Array.isArray(a.screenshots)
               ? a.screenshots.map((s) => ({ path: s.url, url: s.url, alt: s.alt || '' }))
               : []
         );
         setReleases(Array.isArray(a.releases) ? a.releases : []);
         if (a.logoImage) {
            setLogoPreview(a.logoImage);
            try {
               const url = new URL(a.logoImage);
               setLogoPath(url.pathname.replace('/uploads/', ''));
            } catch {
               setLogoPath(a.logoImage.replace(/^.*\/uploads\//, ''));
            }
         }
      } catch {
         toast.error('Failed to load app');
         navigate('/admin/apps');
      } finally {
         setLoading(false);
      }
   };

   const set = (key) => (e) => setFormData((prev) => ({ ...prev, [key]: e.target.value }));

   const handleLogoUpload = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      try {
         setUploading(true);
         const res = await appService.uploadLogo(file);
         setLogoPath(res.data.path);
         setLogoPreview(res.data.url);
      } catch {
         toast.error('Failed to upload logo');
      } finally {
         setUploading(false);
      }
   };

   const handleScreenshotUpload = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      try {
         setUploadingScreenshot(true);
         const res = await appService.uploadLogo(file); // generic image upload, reused
         setScreenshots((prev) => [...prev, { path: res.data.path, url: res.data.url, alt: '' }]);
      } catch {
         toast.error('Failed to upload screenshot');
      } finally {
         setUploadingScreenshot(false);
         if (screenshotInputRef.current) screenshotInputRef.current.value = '';
      }
   };

   const updateScreenshotAlt = (index, alt) => {
      setScreenshots((prev) => prev.map((s, i) => (i === index ? { ...s, alt } : s)));
   };

   const removeScreenshot = (index) => {
      setScreenshots((prev) => prev.filter((_, i) => i !== index));
   };

   const addFeature = () => setFeatures((prev) => [...prev, { title: '', description: '' }]);
   const updateFeature = (index, key, value) =>
      setFeatures((prev) => prev.map((f, i) => (i === index ? { ...f, [key]: value } : f)));
   const removeFeature = (index) => setFeatures((prev) => prev.filter((_, i) => i !== index));

   const handleSubmit = async () => {
      if (!formData.name.trim()) {
         toast.error('App name is required');
         return;
      }
      if (!formData.content.trim()) {
         toast.error('App description is required');
         return;
      }

      const cleanFeatures = features
         .map((f) => ({ title: f.title.trim(), description: f.description.trim() }))
         .filter((f) => f.title && f.description);

      const payload = {
         name: formData.name.trim(),
         slug: formData.slug.trim() || undefined,
         tagline: formData.tagline.trim() || null,
         content: formData.content.trim(),
         disclaimer: formData.disclaimer.trim() || null,
         features: cleanFeatures,
         screenshots: screenshots.map((s) => ({ url: s.path, alt: s.alt || '' })),
         logoImage: logoPath || null,
         status: formData.status,
         repoUrl: formData.repoUrl.trim() || null,
         order: parseInt(formData.order, 10) || 0,
         showInProjects: formData.showInProjects,
      };

      try {
         setSaving(true);
         if (isEdit) {
            await appService.update(id, payload);
            toast.success('App updated successfully');
            navigate('/admin/apps');
         } else {
            const res = await appService.create(payload);
            toast.success('App created successfully');
            navigate(`/admin/apps/${res.data.id}`);
         }
      } catch (error) {
         const msg =
            error.response?.data?.errors?.map((e) => e.msg).join(', ') ||
            error.response?.data?.message ||
            'Failed to save app';
         toast.error(msg);
      } finally {
         setSaving(false);
      }
   };

   const handleAddRelease = async () => {
      if (!newRelease.version.trim()) {
         toast.error('Version is required');
         return;
      }
      if (!newRelease.file) {
         toast.error('Select a package file to upload');
         return;
      }

      try {
         setAddingRelease(true);
         setReleaseUploadProgress(0);
         const res = await appReleaseService.addRelease(id, newRelease, setReleaseUploadProgress);
         setReleases((prev) => [res.data, ...prev]);
         setNewRelease(emptyRelease);
         if (packageInputRef.current) packageInputRef.current.value = '';
         toast.success('Release added successfully');
      } catch (error) {
         toast.error(error.response?.data?.message || 'Failed to add release');
      } finally {
         setAddingRelease(false);
         setReleaseUploadProgress(null);
      }
   };

   const handleReleaseStatusChange = async (releaseId, status) => {
      try {
         await appReleaseService.updateRelease(releaseId, { status });
         setReleases((prev) => prev.map((r) => (r.id === releaseId ? { ...r, status } : r)));
      } catch {
         toast.error('Failed to update release status');
      }
   };

   const handleDeleteRelease = async () => {
      if (!deleteReleaseTarget) return;
      try {
         await appReleaseService.deleteRelease(deleteReleaseTarget.id);
         setReleases((prev) => prev.filter((r) => r.id !== deleteReleaseTarget.id));
         toast.success('Release deleted successfully');
      } catch {
         toast.error('Failed to delete release');
      } finally {
         setDeleteReleaseTarget(null);
      }
   };

   if (loading) {
      return (
         <PanelLayout>
            <Loader />
         </PanelLayout>
      );
   }

   const initials = formData.name
      ? formData.name
           .split(' ')
           .slice(0, 2)
           .map((w) => w[0])
           .join('')
           .toUpperCase()
      : null;

   return (
      <PanelLayout>
         <div className="space-y-6">
            {/* Page header */}
            <div className="flex items-center justify-between">
               <div>
                  <button
                     onClick={() => navigate('/admin/apps')}
                     className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-2 transition-colors"
                  >
                     <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                           strokeLinecap="round"
                           strokeLinejoin="round"
                           strokeWidth={2}
                           d="M15 19l-7-7 7-7"
                        />
                     </svg>
                     Apps
                  </button>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                     {isEdit ? 'Edit App' : 'New App'}
                  </h1>
               </div>
               <div className="flex gap-3">
                  <Button
                     variant="outline"
                     onClick={() => navigate('/admin/apps')}
                     disabled={saving}
                  >
                     Cancel
                  </Button>
                  <Button onClick={handleSubmit} disabled={saving || uploading}>
                     {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create App'}
                  </Button>
               </div>
            </div>

            {/* Identity card */}
            <SectionCard title="App Identity">
               <div className="flex gap-6">
                  {/* Logo upload zone */}
                  <div className="flex-shrink-0 flex flex-col justify-center">
                     <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                     />
                     <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="group relative w-[120px] h-[120px] rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 hover:border-[#0DCAF0] hover:bg-gray-100 dark:hover:bg-gray-700 transition-all overflow-hidden focus:outline-none focus:ring-2 focus:ring-[#0DCAF0]"
                     >
                        {uploading ? (
                           <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-gray-800/80">
                              <svg
                                 className="w-6 h-6 text-[#0DCAF0] animate-spin"
                                 fill="none"
                                 viewBox="0 0 24 24"
                              >
                                 <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                 />
                                 <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                 />
                              </svg>
                           </div>
                        ) : logoPreview ? (
                           <>
                              <img
                                 src={logoPreview}
                                 alt="Logo"
                                 className="w-full h-full object-contain p-2"
                              />
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1">
                                 <span className="text-white text-xs font-medium">Change</span>
                              </div>
                           </>
                        ) : initials ? (
                           <>
                              <span className="text-3xl font-bold text-gray-400 dark:text-gray-500">
                                 {initials}
                              </span>
                              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1">
                                 <span className="text-white text-xs font-medium">Upload</span>
                              </div>
                           </>
                        ) : (
                           <div className="flex flex-col items-center justify-center gap-2">
                              <span className="text-xs text-gray-400 group-hover:text-[#0DCAF0] transition-colors font-medium leading-tight text-center px-2">
                                 Upload Logo
                              </span>
                           </div>
                        )}
                     </button>
                     <p className="mt-2 text-center text-xs text-gray-400 dark:text-gray-500">
                        PNG, JPG, SVG
                     </p>
                  </div>

                  {/* Name, Slug, Status */}
                  <div className="flex-1 flex flex-col gap-4">
                     <div>
                        <Label required>Name</Label>
                        <Input
                           value={formData.name}
                           onChange={set('name')}
                           placeholder="e.g. Quick Todo"
                        />
                     </div>
                     <div className="grid grid-cols-2 gap-4">
                        <div>
                           <Label>Slug</Label>
                           <Input
                              value={formData.slug}
                              onChange={set('slug')}
                              placeholder="auto-generated if left blank"
                           />
                        </div>
                        <div>
                           <SearchableSelect
                              label="Status"
                              searchable={false}
                              options={STATUS_OPTIONS}
                              value={formData.status}
                              onChange={(value) =>
                                 setFormData((prev) => ({ ...prev, status: value }))
                              }
                           />
                        </div>
                     </div>
                     <div>
                        <Label>Tagline</Label>
                        <Input
                           value={formData.tagline}
                           onChange={set('tagline')}
                           placeholder="One-line summary shown in listings"
                        />
                     </div>
                  </div>
               </div>
            </SectionCard>

            {/* About */}
            <SectionCard title="About">
               <div>
                  <Label required>Description</Label>
                  <textarea
                     value={formData.content}
                     onChange={set('content')}
                     placeholder="Full description of the app (HTML allowed)…"
                     rows={6}
                     className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-dark-primary px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#0DCAF0] focus:border-transparent resize-none"
                  />
               </div>
               <div className="mt-4">
                  <Label>Disclaimer</Label>
                  <textarea
                     value={formData.disclaimer}
                     onChange={set('disclaimer')}
                     placeholder="e.g. This app was built primarily for my own personal use and needs. I thought it might help someone else too, so I'm sharing it."
                     rows={3}
                     className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-dark-primary px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#0DCAF0] focus:border-transparent resize-none"
                  />
                  <p className="mt-1.5 text-xs text-gray-400 dark:text-gray-500">
                     Optional - shown as a small note on the app's public page. Leave blank to hide
                     it.
                  </p>
               </div>
            </SectionCard>

            {/* Features */}
            <SectionCard title="Features">
               <div className="space-y-3">
                  {features.map((feature, i) => (
                     <div key={i} className="flex gap-3 items-start">
                        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                           <Input
                              value={feature.title}
                              onChange={(e) => updateFeature(i, 'title', e.target.value)}
                              placeholder="Feature title"
                           />
                           <Input
                              value={feature.description}
                              onChange={(e) => updateFeature(i, 'description', e.target.value)}
                              placeholder="Feature description"
                           />
                        </div>
                        <button
                           type="button"
                           onClick={() => removeFeature(i)}
                           className="mt-2 text-gray-400 hover:text-red-500 transition-colors"
                           aria-label="Remove feature"
                        >
                           ✕
                        </button>
                     </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={addFeature}>
                     + Add Feature
                  </Button>
               </div>
            </SectionCard>

            {/* Screenshots */}
            <SectionCard title="Screenshots">
               <div className="space-y-3">
                  {screenshots.map((shot, i) => (
                     <div key={i} className="flex gap-3 items-center">
                        <img
                           src={shot.url}
                           alt=""
                           className="w-16 h-16 object-cover rounded-lg border border-gray-200 dark:border-gray-700 flex-shrink-0"
                        />
                        <Input
                           value={shot.alt}
                           onChange={(e) => updateScreenshotAlt(i, e.target.value)}
                           placeholder="Alt text (for accessibility/SEO)"
                        />
                        <button
                           type="button"
                           onClick={() => removeScreenshot(i)}
                           className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                           aria-label="Remove screenshot"
                        >
                           ✕
                        </button>
                     </div>
                  ))}
                  <input
                     ref={screenshotInputRef}
                     type="file"
                     accept="image/*"
                     onChange={handleScreenshotUpload}
                     className="hidden"
                  />
                  <Button
                     variant="outline"
                     size="sm"
                     onClick={() => screenshotInputRef.current?.click()}
                     disabled={uploadingScreenshot}
                  >
                     {uploadingScreenshot ? 'Uploading...' : '+ Add Screenshot'}
                  </Button>
               </div>
            </SectionCard>

            {/* Links & Settings */}
            <SectionCard title="Links & Settings">
               <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <IconInput
                     label="Repository URL"
                     value={formData.repoUrl}
                     onChange={set('repoUrl')}
                     placeholder="github.com/org/repo"
                     icon={
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                           <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
                        </svg>
                     }
                  />
                  <div>
                     <Label>Sort Order</Label>
                     <Input
                        type="number"
                        value={formData.order}
                        onChange={set('order')}
                        placeholder="0"
                     />
                  </div>
               </div>
               <div className="flex items-center justify-between gap-4 mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                  <div>
                     <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        Show in Projects
                     </p>
                     <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        Also surface this app as a card on the public Projects page
                     </p>
                  </div>
                  <button
                     type="button"
                     onClick={() =>
                        setFormData((prev) => ({ ...prev, showInProjects: !prev.showInProjects }))
                     }
                     className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-dark-accent focus:ring-offset-2 ${
                        formData.showInProjects ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                     }`}
                     role="switch"
                     aria-checked={formData.showInProjects}
                  >
                     <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                           formData.showInProjects ? 'translate-x-6' : 'translate-x-1'
                        }`}
                     />
                  </button>
               </div>
            </SectionCard>

            {/* Releases - only once the app has a saved id */}
            {isEdit && (
               <SectionCard title="Releases">
                  {releases.length > 0 ? (
                     <div className="space-y-3 mb-6">
                        {releases.map((release) => (
                           <div
                              key={release.id}
                              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 dark:border-gray-700 p-4"
                           >
                              <div>
                                 <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                    v{release.version} · {release.packageType} ·{' '}
                                    {release.architecture}
                                 </p>
                                 <p className="text-xs text-gray-500 dark:text-gray-400">
                                    {formatBytes(release.fileSize)} · {release.downloadCount}{' '}
                                    downloads
                                    {release.releaseNotes ? ` · ${release.releaseNotes}` : ''}
                                 </p>
                              </div>
                              <div className="flex items-center gap-2">
                                 <div className="w-36">
                                    <SearchableSelect
                                       label=""
                                       searchable={false}
                                       options={RELEASE_STATUS_OPTIONS}
                                       value={release.status}
                                       onChange={(value) =>
                                          handleReleaseStatusChange(release.id, value)
                                       }
                                       className="!mb-0"
                                    />
                                 </div>
                                 <button
                                    type="button"
                                    onClick={() => setDeleteReleaseTarget(release)}
                                    className="text-gray-400 hover:text-red-500 transition-colors p-1"
                                    aria-label="Delete release"
                                 >
                                    ✕
                                 </button>
                              </div>
                           </div>
                        ))}
                     </div>
                  ) : (
                     <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                        No releases yet - add the first package below.
                     </p>
                  )}

                  <div className="border-t border-gray-200 dark:border-gray-700 pt-5">
                     <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                        Add Release
                     </h3>
                     <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                        <div>
                           <Label required>Version</Label>
                           <Input
                              value={newRelease.version}
                              onChange={(e) =>
                                 setNewRelease((prev) => ({ ...prev, version: e.target.value }))
                              }
                              placeholder="0.1.0"
                           />
                        </div>
                        <div>
                           <SearchableSelect
                              label="Package Type"
                              searchable={false}
                              options={PACKAGE_TYPE_OPTIONS}
                              value={newRelease.packageType}
                              onChange={(value) =>
                                 setNewRelease((prev) => ({ ...prev, packageType: value }))
                              }
                           />
                        </div>
                        <div>
                           <Label>Architecture</Label>
                           <Input
                              value={newRelease.architecture}
                              onChange={(e) =>
                                 setNewRelease((prev) => ({
                                    ...prev,
                                    architecture: e.target.value,
                                 }))
                              }
                              placeholder="amd64"
                           />
                        </div>
                     </div>
                     <div className="mb-4">
                        <Label>Release Notes</Label>
                        <Input
                           value={newRelease.releaseNotes}
                           onChange={(e) =>
                              setNewRelease((prev) => ({ ...prev, releaseNotes: e.target.value }))
                           }
                           placeholder="What changed in this version?"
                        />
                     </div>
                     <div className="mb-4">
                        <Label required>Package File</Label>
                        <input
                           ref={packageInputRef}
                           type="file"
                           onChange={(e) =>
                              setNewRelease((prev) => ({
                                 ...prev,
                                 file: e.target.files[0] || null,
                              }))
                           }
                           className="block w-full text-sm text-gray-600 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-gray-100 dark:file:bg-gray-700 file:text-gray-700 dark:file:text-gray-200 hover:file:bg-gray-200 dark:hover:file:bg-gray-600"
                        />
                     </div>
                     {releaseUploadProgress !== null && (
                        <div className="mb-4">
                           <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                              <div
                                 className="h-full bg-[#0DCAF0] transition-all"
                                 style={{ width: `${releaseUploadProgress}%` }}
                              />
                           </div>
                           <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              Uploading… {releaseUploadProgress}%
                           </p>
                        </div>
                     )}
                     <Button onClick={handleAddRelease} disabled={addingRelease}>
                        {addingRelease ? 'Uploading...' : 'Add Release'}
                     </Button>
                  </div>
               </SectionCard>
            )}
         </div>

         <ConfirmModal
            isOpen={!!deleteReleaseTarget}
            title="Delete Release"
            message={`Are you sure you want to delete v${deleteReleaseTarget?.version}? The package file will be permanently removed. This action cannot be undone.`}
            onConfirm={handleDeleteRelease}
            onCancel={() => setDeleteReleaseTarget(null)}
         />
      </PanelLayout>
   );
};

export default AppForm;
